import {
  resolveRoleId,
  type RoleId,
  type RoleName
} from '@/roles';
import { validatePermissionCode, type PermissionCode } from '@/constants/permissions';
import { DEFAULT_DEPARTMENT_NAME } from '@/constants/departments';

export interface RawUserPayload {
  accessToken?: string;
  refreshToken?: string;
  token?: string;
  fullname?: string;
  isSetup?: unknown;
  position?: string;
  about?: string;
  departmentId?: string | null;
  departmentName?: string | null;
  permissions?: unknown;
  [key: string]: unknown;
}

export interface TokenPayload {
  id?: string;
  fullname?: string;
  email?: string;
  role?: RoleId | string | number;
  roleId?: RoleId | string | number;
  roleName?: RoleName | string;
  isSetup?: unknown;
  position?: string;
  about?: string;
  departmentId?: string | null;
  departmentName?: string | null;
  permissions?: unknown;
  [key: string]: unknown;
}

export interface User {
  fullname: string;
  token: string;
  roleId: RoleId;
  roleName: RoleName;
  isSetup: number;
  position?: string;
  about?: string;
  departmentId?: string | null;
  departmentName?: string | null;
  permissions: PermissionCode[];
  [key: string]: unknown;
}

export interface NormalizedAuthData {
  user: User;
  accessToken: string;
  refreshToken: string;
}

const decodeJwtPayload = <T = Record<string, unknown>>(token: string): T | null => {
  try {
    const [, payload = ''] = token.split('.');
    if (!payload) {
      return null;
    }
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = decodeURIComponent(
      atob(normalized)
        .split('')
        .map(char => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(decoded) as T;
  } catch (error) {
    console.warn('Failed to decode JWT payload', error);
    return null;
  }
};

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? fallback : value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
};

const normalizePermissions = (value: unknown): PermissionCode[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const deduped = new Set<PermissionCode>();

  for (const entry of value) {
    if (typeof entry === 'string' && validatePermissionCode(entry)) {
      deduped.add(entry);
    }
  }

  return Array.from(deduped);
};

const buildUserFromToken = (accessToken: string, payload: TokenPayload, responsePayload?: RawUserPayload): User | null => {
  const fullname = (payload.fullname as string) ?? (responsePayload?.fullname as string);
  if (!fullname) {
    return null;
  }

  // Use role data directly from JWT token
  const roleId = resolveRoleId(payload.roleId ?? payload.role, null);
  if (!roleId) {
    console.warn('Invalid role ID in JWT token');
    return null;
  }

  // Use role name directly from JWT token
  const roleName = (payload.roleName as string) ?? 'unknown';

  const isSetupFallback = responsePayload?.isSetup ?? payload.isSetup;
  const sanitizedResponse: Record<string, unknown> = { ...(responsePayload ?? {}) };

  const departmentId = (payload.departmentId as string | null | undefined) ?? (responsePayload?.departmentId as string | null | undefined) ?? null;
  const departmentName =
    (payload.departmentName as string | null | undefined) ??
    (responsePayload?.departmentName as string | null | undefined) ??
    DEFAULT_DEPARTMENT_NAME;
  const permissions = normalizePermissions(
    Array.isArray(payload.permissions) ? payload.permissions : responsePayload?.permissions
  );

  delete sanitizedResponse.accessToken;
  delete sanitizedResponse.refreshToken;
  delete sanitizedResponse.token;

  return {
    ...sanitizedResponse,
    ...payload,
    fullname,
    token: accessToken,
    isSetup: toNumber(isSetupFallback),
    position: (payload.position as string) ?? (responsePayload?.position as string) ?? undefined,
    about: (payload.about as string) ?? (responsePayload?.about as string) ?? undefined,
    roleId,
    roleName,
    departmentId,
    departmentName,
    permissions
  };
};

export const normalizeAuthPayload = (
  responsePayload: RawUserPayload | undefined,
  fallbackAccessToken?: string | null,
  fallbackRefreshToken?: string | null
): NormalizedAuthData | null => {
  const accessToken =
    responsePayload?.accessToken ??
    responsePayload?.token ??
    fallbackAccessToken ??
    null;
  const refreshToken =
    responsePayload?.refreshToken ??
    fallbackRefreshToken ??
    null;

  if (!accessToken || !refreshToken) {
    return null;
  }

  const tokenPayload = decodeJwtPayload<TokenPayload>(accessToken);
  if (!tokenPayload) {
    return null;
  }

  const user = buildUserFromToken(accessToken, tokenPayload, responsePayload);

  if (!user) {
    return null;
  }

  return {
    user,
    accessToken,
    refreshToken
  };
};

export const decodeUserFromToken = (accessToken: string | null | undefined): User | null => {
  if (!accessToken) {
    return null;
  }

  const payload = decodeJwtPayload<TokenPayload>(accessToken);
  if (!payload) {
    return null;
  }

  const user = buildUserFromToken(accessToken, payload);
  if (!user) {
    return null;
  }

  return user;
};
